import type { GradeCreditRow } from '@/types/grade-credit-row';
import { sanitizeCreditsFromSource } from '@/utils/sanitize-credits-input';

/**
 * Shared parsing / row checks for the grade min/max credits table (configurations + plan of study).
 * Kept aligned with GradeCreditRangesTable: persisted min/max values are stringified with
 * {@link sanitizeCreditsFromSource}, and live cell edits go through the same sanitizer on change.
 */

/** Parse a single cell; empty or non-finite → null (same semantics as configurations `parseNum`). */
export function parseGradeCreditTableNumber(s: string): number | null {
  const t = s.trim();
  if (t === '') {
    return null;
  }
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** True when both min and max are numeric and min > max (configurations panel row error). */
export function gradeCreditStringsMinExceedsMax(minStr: string, maxStr: string): boolean {
  const minNum = parseGradeCreditTableNumber(minStr);
  const maxNum = parseGradeCreditTableNumber(maxStr);
  return minNum !== null && maxNum !== null && minNum > maxNum;
}

export type GradeCreditStringRow = { min: string; max: string };

/** Grades with min > max when both sides parse as numbers. */
export function gradesWithCreditOrderErrors(
  rows: Record<number, GradeCreditStringRow>,
  gradeList: readonly number[]
): number[] {
  const errors: number[] = [];
  for (const grade of gradeList) {
    const row = rows[grade] ?? { min: '', max: '' };
    if (gradeCreditStringsMinExceedsMax(row.min, row.max)) {
      errors.push(grade);
    }
  }
  return errors;
}

/** True if any row has both min and max set and min > max (matches configurations save guard). */
export function gradeCreditRowsHaveInvalidOrder(rows: GradeCreditRow[]): boolean {
  return rows.some((row) => {
    const { minCredits: min, maxCredits: max } = row;
    return min !== null && min !== undefined && max !== null && max !== undefined && min > max;
  });
}

/** True if any row has a negative min or max (aligns with {@link validateCreditRange} non-negative rule). */
export function gradeCreditRowsHaveNegativeCredits(rows: GradeCreditRow[]): boolean {
  return rows.some((row) => {
    const { minCredits: min, maxCredits: max } = row;
    return (
      (min !== null && min !== undefined && min < 0) ||
      (max !== null && max !== undefined && max < 0)
    );
  });
}

/** Invalid credit section: negative values or min > max when both set. */
export function gradeCreditRowsInvalidForCreditsSection(rows: GradeCreditRow[]): boolean {
  return gradeCreditRowsHaveInvalidOrder(rows) || gradeCreditRowsHaveNegativeCredits(rows);
}

/** String cells for {@link GradeCreditRangesTable} from numeric plan form rows. */
export function gradeCreditRowsToStringRecord(
  rows: GradeCreditRow[],
  gradeList: readonly number[]
): Record<number, GradeCreditStringRow> {
  const byGrade = new Map(rows.map((r) => [r.grade, r]));
  return gradeList.reduce(
    (acc, grade) => {
      const r = byGrade.get(grade);
      const minC = r?.minCredits;
      const maxC = r?.maxCredits;
      acc[grade] = {
        min: minC !== null && minC !== undefined ? sanitizeCreditsFromSource(minC) : '',
        max: maxC !== null && maxC !== undefined ? sanitizeCreditsFromSource(maxC) : '',
      };
      return acc;
    },
    {} as Record<number, GradeCreditStringRow>
  );
}
